import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { ChatInterface } from '../components/ai-tool/ChatInterface';
import { AIAdminPanel } from '../components/ai-tool/AIAdminPanel';
import { AGENT_URL, setAgentToken } from '../utils/agentFetch';
import client from '../api/client';

type SsoState = 'loading' | 'ready' | 'error';

interface AgentUserInfo {
  role: string;
  org_id: string;
  display_name: string;
  email: string;
}

export const AIToolSection: React.FC = () => {
  useAuth();
  const [ssoState, setSsoState] = useState<SsoState>('loading');
  const ssoFetchedRef = useRef(false);
  const [agentUser, setAgentUser] = useState<AgentUserInfo | null>(null);
  const [showAdminPanel, setShowAdminPanel] = useState(false);

  // SSO: get signed token from website backend, exchange it directly with the agent
  useEffect(() => {
    if (ssoFetchedRef.current) return;
    ssoFetchedRef.current = true;

    (async () => {
      try {
        // Step 1 — get the SSO URL (contains the HMAC-signed token)
        const { data } = await client.post<{ ssoUrl: string }>('/ai-tool/sso');
        const tokenMatch = data.ssoUrl.match(/[?&]token=([^&]+)/);
        if (!tokenMatch) throw new Error('No token in SSO URL');

        // Step 2 — exchange the token directly with the agent (no iframe)
        const r = await fetch(`${AGENT_URL}/auth/sso-exchange`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: tokenMatch[1] }),
        });

        if (!r.ok) throw new Error(`SSO exchange failed: ${r.status}`);

        const result = await r.json();
        setAgentToken(result.session_token);
        setAgentUser(result.user);
        setSsoState('ready');
      } catch (e) {
        console.error('SSO error:', e);
        setSsoState('error');
      }
    })();
  }, []);

  const retry = () => {
    ssoFetchedRef.current = false;
    setSsoState('loading');
    // Re-trigger by forcing a re-render
    setSsoState('loading');
    ssoFetchedRef.current = false;
    // Re-run SSO
    (async () => {
      try {
        const { data } = await client.post<{ ssoUrl: string }>('/ai-tool/sso');
        const tokenMatch = data.ssoUrl.match(/[?&]token=([^&]+)/);
        if (!tokenMatch) throw new Error('No token in SSO URL');

        const r = await fetch(`${AGENT_URL}/auth/sso-exchange`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: tokenMatch[1] }),
        });

        if (!r.ok) throw new Error(`SSO exchange failed: ${r.status}`);

        const result = await r.json();
        setAgentToken(result.session_token);
        setAgentUser(result.user);
        setSsoState('ready');
      } catch (e) {
        console.error('SSO retry error:', e);
        setSsoState('error');
      }
    })();
  };

  const isAdmin = agentUser?.role === 'org_admin' || agentUser?.role === 'super_admin';
  const isSuperAdmin = agentUser?.role === 'super_admin';

  return (
    <div className="ai-tool-section">
      {ssoState === 'loading' && (
        <div className="ai-loading-state">
          <p>Connecting to AI Assistant…</p>
        </div>
      )}

      {ssoState === 'error' && (
        <div className="ai-error-state">
          <p>Could not connect to the AI Assistant.</p>
          <button className="btn btn-primary" onClick={retry}>Try again</button>
        </div>
      )}

      {ssoState === 'ready' && (
        <>
          <div className="ai-tool-header">
            <div>
              <h2 style={{ marginBottom: 4 }}>RDF AI Assistant</h2>
              <p style={{ color: 'var(--c-text-muted)', fontSize: 14 }}>
                Ask questions about RDF documents and regulatory guidance, powered by AI.
              </p>
            </div>
            {isAdmin && (
              <button
                className="btn btn-outline btn-sm"
                onClick={() => setShowAdminPanel(p => !p)}
              >
                {showAdminPanel ? '← Back to Chat' : '⚙ All Usage'}
              </button>
            )}
          </div>

          {showAdminPanel && isAdmin ? (
            <AIAdminPanel isSuperAdmin={isSuperAdmin} />
          ) : (
            <ChatInterface userInfo={agentUser} />
          )}
        </>
      )}
    </div>
  );
};
